/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import "../../../assets/styles/custom-styles.css";
import { addressFields } from "../../../constants/forms/addressFields";
import Form from "../../../components/forms/Form";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import useForm from "../../../hooks/useForm";
import { maskCep, removeMask } from "../../../utils/maskUtils";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import { useMutation, useQuery } from "@tanstack/react-query";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import useSelectOptions from "../../../hooks/useSelectOptions";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const EditOrganizationAddressPage = () => {
  const navigate = useNavigate();
  const { organizationId, addressId } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
  } = useBaseService(navigate);
  const { formData, setFormData, handleChange } = useForm(
    setDefaultFieldValues(addressFields),
  );

  const handleFieldChange = useCallback(
    (fieldId, value, field) => {
      if (fieldId === "zip") {
        handleCepChange(value);
      } else {
        handleChange(fieldId, value);
      }
    },
    [handleChange],
  );

  const handleCepChange = useCallback(
    async (cep) => {
      const value = maskCep(cep);
      const zip = removeMask(value);

      setFormData((prev) => ({
        ...prev,
        zip: value,
      }));

      if (zip.length === 8) {
        showLoader();
        try {
          const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
          if (!response.ok) throw new Error("Erro ao buscar o CEP");

          const data = await response.json();
          if (data.erro) throw new Error("CEP não encontrado");

          setFormData((prev) => ({
            ...prev,
            street: data.logradouro || "",
            district: data.bairro || "",
            city: data.localidade || "",
            state: data.uf || "",
            country: "Brasil",
          }));
        } catch (error) {
          showNotification("error", error.message);
        } finally {
          hideLoader();
        }
      }
    },
    [setFormData, showNotification],
  );

  const {
    data: addressData,
    isLoading: isLoadingAddress,
    isError: isErrorAddress,
    error: addressQueryError,
  } = useQueryWithLoader({
    queryKey: ["addressOrganization", organizationId, addressId],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.organizations.addresses.getById(organizationId, addressId),
      );
      return response.result;
    },
    enabled: !!organizationId && !!addressId,
  });

  useEffect(() => {
    if (!isLoadingAddress && addressData) {
      hideLoader();
      setFormData({
        address: {
          alias: addressData.alias,
          zip: maskCep(addressData.zip),
          street: addressData.street,
          district: addressData.district,
          city: addressData.city,
          state: {
            value: addressData.state === "CE" && "Ceará",
            label: addressData.state === "CE" && "Ceará",
          },
          country: {
            value: addressData.country === "BR" && "Brasil",
            label: addressData.country === "BR" && "Brasil",
          },
          number: addressData.number,
        },
      });
    }
    if (isErrorAddress && addressQueryError) {
      hideLoader();
      console.error("Erro ao buscar endereço:", addressQueryError);
      showNotification(
        "error",
        `Erro ao buscar endereço: ${addressQueryError?.message}`,
      );
      navigate(`/aluguel/organizacoes/detalhes/${organizationId}`);
    }
  }, [
    addressData,
    isLoadingAddress,
    isErrorAddress,
    addressQueryError,
    hideLoader,
    setFormData,
    showNotification,
    navigate,
    organizationId,
  ]);

  const stateSelect = useRemoteSelectOptions({
    queryKey: ["stateOptions"],
    fetcher: async () =>
      await fetch(`https://brasilapi.com.br/api/ibge/uf/v1`).then((res) =>
        res.json(),
      ),
    fieldId: "address.state",
    formData,
    isMulti: false,
    mapToOption: (type) => ({ value: type.nome, label: type.nome }),
  });

  const rawCountryOptions = useMemo(
    () => [{ value: "Brasil", label: "Brasil" }],
    [],
  );

  const countrySelect = useSelectOptions({
    fieldId: "address.country",
    formData,
    rawOptions: rawCountryOptions,
    isMulti: false,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
  });

  const selects = {
    "address.state": stateSelect,
    "address.country": countrySelect,
  };

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  const update = useMutation({
    mutationFn: async (data) => {
      const response = await baseServicePut(
        entities.organizations.addresses.update(organizationId, addressId),
        data,
      );
      return response.result;
    },
    onSuccess: () => {
      showNotification("success", "Endereço atualizado com sucesso.");
      navigate(`/aluguel/organizacoes/detalhes/${organizationId}`);
    },
    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const sanitizedData = {
        ...formData,
        address: {
          ...formData.address,
          addressable_type: "organization",
          number: formData.address.number ? String(formData.address.number) : null,
          zip: formData.address.zip ? removeMask(formData.address.zip) : "",
        },
      };

      return await update.mutateAsync(sanitizedData);
    } catch (error) {
      console.error("Erro ao atualizar endereço:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [
    update,
    organizationId,
    addressId,
    navigate,
    showLoader,
    hideLoader,
    showNotification,
  ]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/organizacoes/detalhes/${organizationId}`);
  }, [navigate, organizationId]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <div className="container-fluid p-1">
        <PageHeader
          title="Edição de Endereço da Organização"
          showBackButton={true}
          backUrl={`/aluguel/organizacoes/detalhes/${organizationId}/`}
        />

        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Atualizar Endereço"
          textLoadingSubmit="Atualizando..."
          handleBack={handleBack}
        >
          {() => {
            const renderedSections = new Set();

            return addressFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleFieldChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditOrganizationAddressPage;
